<!-- eslint-disable vue/no-unused-vars -->
<template>
  <div class="turnover-dashboard">
    <!-- Header -->
    <b-card class="mb-2 header-card">
      <div class="bubbles">
        <span
          v-for="n in 10"
          :key="n"
          class="bubble"
        />
      </div>
      <div class="d-flex justify-content-between align-items-center header-content">
        <div>
          <h4 class="mb-0">
            <feather-icon
              icon="TrendingUpIcon"
              size="24"
              class="mr-1"
            />
            Analyse du Chiffre d'Affaires
          </h4>
          <small>Suivi mensuel des performances de vente - {{ year }}</small>
        </div>
        <div class="d-flex align-items-center gap-2">
          <b-form-select
            v-model="year"
            :options="yearOptions"
            size="sm"
            class="year-select mr-1"
            @change="fetchMonthlyRecap"
          />
          <b-button
            variant="outline-primary"
            size="sm"
            @click="fetchMonthlyRecap"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              class="mr-50"
            />
            Actualiser
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem;"
      />
      <p class="mt-2 text-muted">
        Chargement des données...
      </p>
    </div>

    <template v-else-if="monthlyRecap.length > 0">
      <!-- Summary Cards -->
      <b-row class="match-height mb-2">
        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="stat-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Chiffre d'Affaires Total
                </p>
                <h3 class="stat-value text-primary">
                  {{ formatCurrency(totalRevenue) }}
                </h3>
                <small class="text-muted">Année {{ year }}</small>
              </div>
              <div class="stat-icon bg-light-primary">
                <feather-icon
                  icon="DollarSignIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="stat-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Total Commandes
                </p>
                <h3 class="stat-value text-info">
                  {{ totalOrders.toLocaleString() }}
                </h3>
                <small class="text-muted">{{ monthlyRecap.length }} mois actifs</small>
              </div>
              <div class="stat-icon bg-light-info">
                <feather-icon
                  icon="PackageIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="stat-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Moyenne Mensuelle
                </p>
                <h3 class="stat-value text-success">
                  {{ formatCurrency(averageRevenue) }}
                </h3>
                <small class="text-muted">Par mois</small>
              </div>
              <div class="stat-icon bg-light-success">
                <feather-icon
                  icon="BarChart2Icon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          sm="12"
        >
          <b-card class="stat-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Meilleur Mois
                </p>
                <h3 class="stat-value text-warning">
                  {{ bestMonth.month || '-' }}
                </h3>
                <small class="text-muted">{{ formatCurrency(bestMonth.revenue) }}</small>
              </div>
              <div class="stat-icon bg-light-warning">
                <feather-icon
                  icon="AwardIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Chart Section -->
      <b-row class="match-height">
        <b-col
          lg="8"
          md="12"
        >
          <b-card>
            <b-card-header class="pb-0">
              <h5>
                <feather-icon
                  icon="ActivityIcon"
                  size="18"
                  class="mr-50"
                />
                Évolution du Chiffre d'Affaires
              </h5>
            </b-card-header>
            <b-card-body>
              <div class="chart-container">
                <div
                  v-for="(item, index) in monthlyRecap"
                  :key="index"
                  class="chart-bar-item"
                >
                  <div class="chart-bar-wrapper">
                    <div
                      class="chart-bar"
                      :style="{ height: getBarHeight(item.price_ttc_sum) + '%' }"
                    >
                      <span
                        v-if="item.price_ttc_sum > 0"
                        class="bar-tooltip"
                      >
                        {{ formatCurrency(item.price_ttc_sum) }}
                      </span>
                    </div>
                  </div>
                  <span class="chart-label">{{ getMonthShort(item.month) }}</span>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <b-col
          lg="4"
          md="12"
        >
          <b-card class="h-100">
            <b-card-header class="pb-0">
              <h5>
                <feather-icon
                  icon="PieChartIcon"
                  size="18"
                  class="mr-50"
                />
                Répartition Mensuelle
              </h5>
            </b-card-header>
            <b-card-body>
              <div class="distribution-list">
                <div
                  v-for="(item, index) in topMonths"
                  :key="index"
                  class="distribution-item"
                >
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <div class="d-flex align-items-center">
                      <div
                        class="rank-badge"
                        :class="getRankClass(index)"
                      >
                        {{ index + 1 }}
                      </div>
                      <span class="ml-1 font-weight-bold">{{ item.month }}</span>
                    </div>
                    <span class="text-primary font-weight-bold">{{ formatCurrency(item.price_ttc_sum) }}</span>
                  </div>
                  <b-progress
                    :value="getPercentage(item.price_ttc_sum)"
                    max="100"
                    height="6px"
                    :variant="getProgressVariant(index)"
                  />
                  <div class="d-flex justify-content-between mt-1">
                    <small class="text-muted">{{ item.order_count }} commandes</small>
                    <small class="text-muted">{{ getPercentage(item.price_ttc_sum).toFixed(1) }}%</small>
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Detailed Table -->
      <b-row class="mt-2">
        <b-col cols="12">
          <b-card>
            <b-card-header class="pb-0">
              <h5>
                <feather-icon
                  icon="ListIcon"
                  size="18"
                  class="mr-50"
                />
                Détail par Mois
              </h5>
            </b-card-header>
            <b-card-body>
              <b-table
                :items="monthlyRecap"
                :fields="tableFields"
                responsive
                hover
                class="modern-table"
              >
                <template #cell(month)="data">
                  <div class="d-flex align-items-center">
                    <div class="month-icon">
                      <feather-icon
                        icon="CalendarIcon"
                        size="16"
                      />
                    </div>
                    <span class="font-weight-bold ml-1">{{ data.item.month }}</span>
                  </div>
                </template>
                <template #cell(order_count)="data">
                  <b-badge
                    variant="light-info"
                    pill
                  >
                    {{ data.item.order_count.toLocaleString() }} commandes
                  </b-badge>
                </template>
                <template #cell(price_ttc_sum)="data">
                  <span class="font-weight-bold text-primary">
                    {{ formatCurrency(data.item.price_ttc_sum) }}
                  </span>
                </template>
                <template #cell(percentage)="data">
                  <div class="d-flex align-items-center">
                    <b-progress
                      :value="getPercentage(data.item.price_ttc_sum)"
                      max="100"
                      height="8px"
                      variant="primary"
                      style="width: 100px;"
                      class="mr-1"
                    />
                    <span class="text-muted">{{ getPercentage(data.item.price_ttc_sum).toFixed(1) }}%</span>
                  </div>
                </template>
                <template #cell(average)="data">
                  <span class="text-muted">
                    {{ formatCurrency(data.item.price_ttc_sum / data.item.order_count) }}/cmd
                  </span>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <!-- Empty State -->
    <div
      v-else-if="!isLoading"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-2"
      />
      <h5 class="text-muted">
        Aucune donnée disponible
      </h5>
      <p class="text-muted">
        Les données de chiffre d'affaires apparaîtront ici.
      </p>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BSpinner,
  BCol,
  BRow,
  BButton,
  BProgress,
  BBadge,
  BTable,
  BFormSelect,
} from 'bootstrap-vue'

import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  name: 'TurnoverDashboard',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BSpinner,
    BCol,
    BRow,
    BButton,
    BProgress,
    BBadge,
    BTable,
    BFormSelect,
  },
  data() {
    return {
      isLoading: false,
      user: {},
      monthlyRecap: [],
      year: new Date().getFullYear(),
      tableFields: [
        { key: 'month', label: 'Mois', sortable: true },
        { key: 'order_count', label: 'Commandes', sortable: true },
        { key: 'price_ttc_sum', label: 'Chiffre d\'Affaires', sortable: true },
        { key: 'percentage', label: 'Part du Total' },
        { key: 'average', label: 'Moyenne/Commande' },
      ],
    }
  },
  computed: {
    yearOptions() {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let i = currentYear; i >= currentYear - 5; i -= 1) {
        years.push({ value: i, text: i.toString() })
      }
      return years
    },
    totalRevenue() {
      return this.monthlyRecap.reduce((sum, item) => sum + (item.price_ttc_sum || 0), 0)
    },
    totalOrders() {
      return this.monthlyRecap.reduce((sum, item) => sum + (item.order_count || 0), 0)
    },
    averageRevenue() {
      if (this.monthlyRecap.length === 0) return 0
      return this.totalRevenue / this.monthlyRecap.length
    },
    bestMonth() {
      if (this.monthlyRecap.length === 0) return { month: '-', revenue: 0 }
      const best = this.monthlyRecap.reduce((max, item) => (item.price_ttc_sum > max.price_ttc_sum ? item : max), this.monthlyRecap[0])
      return { month: best.month, revenue: best.price_ttc_sum }
    },
    topMonths() {
      return [...this.monthlyRecap]
        .sort((a, b) => b.price_ttc_sum - a.price_ttc_sum)
        .slice(0, 5)
    },
    maxRevenue() {
      return Math.max(...this.monthlyRecap.map(item => item.price_ttc_sum), 1)
    },
  },
  created() {
    this.user = storeAuth.state.user
    if (this.user.role === 'admin' || this.user.role === 'superadmin') {
      this.fetchMonthlyRecap()
    }
  },
  methods: {
    async fetchMonthlyRecap() {
      try {
        this.isLoading = true
        const { data } = await axios.get(`/api/kpi/orders/monthly-summary/?year=${this.year}`)
        this.monthlyRecap = data
        this.isLoading = false
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching monthly recap:', error)
        this.isLoading = false
      }
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '0.000 TND'
      return `${Number(value).toFixed(3)} TND`
    },
    getBarHeight(value) {
      if (this.maxRevenue === 0) return 0
      return Math.max((value / this.maxRevenue) * 100, 5)
    },
    getPercentage(value) {
      if (this.totalRevenue === 0) return 0
      return (value / this.totalRevenue) * 100
    },
    getMonthShort(month) {
      const shorts = {
        Janvier: 'Jan',
        Février: 'Fév',
        Mars: 'Mar',
        Avril: 'Avr',
        Mai: 'Mai',
        Juin: 'Juin',
        Juillet: 'Juil',
        Août: 'Août',
        Septembre: 'Sep',
        Octobre: 'Oct',
        Novembre: 'Nov',
        Décembre: 'Déc',
      }
      return shorts[month] || month.substring(0, 3)
    },
    getRankClass(index) {
      const classes = ['rank-gold', 'rank-silver', 'rank-bronze', 'rank-default', 'rank-default']
      return classes[index] || 'rank-default'
    },
    getProgressVariant(index) {
      const variants = ['warning', 'secondary', 'info', 'primary', 'success']
      return variants[index] || 'primary'
    },
  },
}
</script>

<style lang="scss" scoped>
@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) scale(1);
    opacity: 0.7;
  }
  50% {
    transform: translateY(-20px) scale(1.1);
    opacity: 1;
  }
}

@keyframes rise {
  0% {
    bottom: -100px;
    transform: translateX(0) scale(0.5);
    opacity: 0;
  }
  10% {
    opacity: 0.8;
  }
  50% {
    transform: translateX(100px) scale(1);
    opacity: 0.5;
  }
  100% {
    bottom: 120%;
    transform: translateX(-50px) scale(0.3);
    opacity: 0;
  }
}

.turnover-dashboard {
  .header-card {
    background: linear-gradient(-45deg, #0A2634, #0d3040, #1a4a5e, #0A2634, #0A2634);
    background-size: 400% 400%;
    animation: gradientShift 8s ease infinite;
    color: white;
    position: relative;
    overflow: hidden;
    border: none;
    box-shadow: 0 10px 40px rgba(10, 38, 52, 0.3);

    .bubbles {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      overflow: hidden;

      .bubble {
        position: absolute;
        bottom: -100px;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 50%;
        animation: rise 8s infinite ease-in;

        &:nth-child(1) {
          width: 40px;
          height: 40px;
          left: 10%;
          animation-duration: 8s;
          animation-delay: 0s;
        }

        &:nth-child(2) {
          width: 20px;
          height: 20px;
          left: 20%;
          animation-duration: 6s;
          animation-delay: 1s;
        }

        &:nth-child(3) {
          width: 50px;
          height: 50px;
          left: 35%;
          animation-duration: 10s;
          animation-delay: 2s;
        }

        &:nth-child(4) {
          width: 80px;
          height: 80px;
          left: 50%;
          animation-duration: 12s;
          animation-delay: 0s;
        }

        &:nth-child(5) {
          width: 35px;
          height: 35px;
          left: 55%;
          animation-duration: 7s;
          animation-delay: 3s;
        }

        &:nth-child(6) {
          width: 45px;
          height: 45px;
          left: 65%;
          animation-duration: 9s;
          animation-delay: 1.5s;
        }

        &:nth-child(7) {
          width: 25px;
          height: 25px;
          left: 75%;
          animation-duration: 6s;
          animation-delay: 2.5s;
        }

        &:nth-child(8) {
          width: 60px;
          height: 60px;
          left: 80%;
          animation-duration: 11s;
          animation-delay: 0.5s;
        }

        &:nth-child(9) {
          width: 15px;
          height: 15px;
          left: 90%;
          animation-duration: 5s;
          animation-delay: 4s;
        }

        &:nth-child(10) {
          width: 30px;
          height: 30px;
          left: 5%;
          animation-duration: 8s;
          animation-delay: 3.5s;
        }
      }
    }

    .header-content {
      position: relative;
      z-index: 1;
    }

    h4, small {
      color: white !important;
      position: relative;
      z-index: 1;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .year-select {
      background: rgba(255, 255, 255, 0.2);
      border: 1px solid rgba(255, 255, 255, 0.5);
      color: white;
      position: relative;
      z-index: 1;

      option {
        color: #333;
      }
    }

    .btn-outline-primary {
      color: white;
      border-color: rgba(255, 255, 255, 0.8);
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
      position: relative;
      z-index: 1;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.25);
        border-color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
      }
    }
  }

  .stat-card {
    border: none;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
    }

    .stat-label {
      font-size: 0.875rem;
      color: #6e6b7b;
      margin-bottom: 0.5rem;
    }

    .stat-value {
      font-size: 1.5rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
    }

    .stat-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .chart-container {
    display: flex;
    justify-content: space-around;
    align-items: flex-end;
    height: 250px;
    padding: 20px 0;
    border-bottom: 2px solid #f3f2f7;

    .chart-bar-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      flex: 1;
      max-width: 60px;

      .chart-bar-wrapper {
        height: 200px;
        width: 100%;
        display: flex;
        align-items: flex-end;
        justify-content: center;

        .chart-bar {
          width: 70%;
          background: linear-gradient(180deg, #28c76f 0%, #48da89 100%);
          border-radius: 8px 8px 0 0;
          position: relative;
          transition: all 0.3s ease;
          cursor: pointer;

          &:hover {
            transform: scaleX(1.1);
            box-shadow: 0 -4px 20px rgba(40, 199, 111, 0.4);

            .bar-tooltip {
              opacity: 1;
              transform: translateX(-50%) translateY(-5px);
            }
          }

          .bar-tooltip {
            position: absolute;
            top: -35px;
            left: 50%;
            transform: translateX(-50%);
            background: #333;
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 0.7rem;
            white-space: nowrap;
            opacity: 0;
            transition: all 0.3s ease;

            &::after {
              content: '';
              position: absolute;
              bottom: -5px;
              left: 50%;
              transform: translateX(-50%);
              border-left: 5px solid transparent;
              border-right: 5px solid transparent;
              border-top: 5px solid #333;
            }
          }
        }
      }

      .chart-label {
        margin-top: 10px;
        font-size: 0.75rem;
        color: #6e6b7b;
        font-weight: 500;
      }
    }
  }

  .distribution-list {
    .distribution-item {
      padding: 12px;
      border-radius: 8px;
      margin-bottom: 12px;
      background: #f8f8f8;
      transition: all 0.3s ease;

      &:hover {
        background: #f0f0f0;
      }

      .rank-badge {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.75rem;
        font-weight: 600;
        color: white;

        &.rank-gold {
          background: linear-gradient(135deg, #ffd700, #ffb347);
        }

        &.rank-silver {
          background: linear-gradient(135deg, #c0c0c0, #a8a8a8);
        }

        &.rank-bronze {
          background: linear-gradient(135deg, #cd7f32, #b87333);
        }

        &.rank-default {
          background: #6e6b7b;
        }
      }
    }
  }

  .modern-table {
    .month-icon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: rgba(40, 199, 111, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #28c76f;
    }
  }

  .bg-light-primary {
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
  }

  .bg-light-success {
    background: rgba(40, 199, 111, 0.12);
    color: #28c76f;
  }

  .bg-light-info {
    background: rgba(0, 207, 232, 0.12);
    color: #00cfe8;
  }

  .bg-light-warning {
    background: rgba(255, 159, 67, 0.12);
    color: #ff9f43;
  }
}
</style>
